<template>
  <div class="h-100">
    <div class="d-flex justify-content px-2 pt-2 align-items-center">
      <div class="d-flex align-items-center mr-auto" v-if="source === 'default' || source === 'template'">
        <button class="btn btn-sm rounded-circle p-25 cursor-pointer" :class="isDark ? 'btn-danger' : 'btn-primary'"
          @click="goBack()">
          <i class="feather icon-arrow-left arrow-icon"></i>
        </button>
        <div class="ml-1 text-capitalize text-dark cursor-pointer goback-text" @click="goBack()">
          {{ $t("go_back") }}
        </div>
      </div>

      <div v-if="errorMessage" class="ml-2">
        <div class="custom-alert-danger rounded d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center mr-2">
            <feather-icon icon="AlertCircleIcon" size="18" class="mr-50 flex-shrink-0" />
            <span class="text-sm font-weight-bold">{{ errorMessage }}</span>
          </div>
          <feather-icon icon="XIcon" size="18" class="cursor-pointer flex-shrink-0" @click="errorMessage = null" />
        </div>
      </div>

      <b-button v-if="source === 'template'" variant="primary" class="ml-auto text-capitalize"
        :disabled="loading || !panel || isSavingTemplate" @click="saveTemplate">
        <b-spinner v-if="isSavingTemplate" small class="mr-50" />
        {{ $t("dmp.templateConfig.saveTemplate") }}
      </b-button>
    </div>

    <!-- Configuration Tabs -->
    <b-card :class="{ 'm-2': source === 'default' || source === 'template' }" no-body>
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" :label="$t('dmp.common.loading')"></b-spinner>
        <p class="mt-2 mb-0">{{ $t("dmp.common.loading") }}...</p>
      </div>

      <div v-else-if="!panel" class="text-center py-5">
        <feather-icon icon="AlertTriangleIcon" size="48" class="text-warning mb-2" />
        <h5 class="text-warning">{{ $t("dmp.common.panelNotFound") }}</h5>
        <p class="text-muted">{{ $t("dmp.common.panelNotFoundMessage") }}</p>
      </div>

      <b-tabs v-else vertical :content-class="{
        'col-12 col-md-9 mt-1 mt-md-0': source === 'default',
        'col-12': source === 'grid',
      }" pills nav-wrapper-class="col-md-3 col-12 px-2 pt-1" nav-class="nav-left" v-model="activeTab">
        <b-tab v-for="(tab, index) in tabsConfig" :key="index" :disabled="isTabDisabled(index)">
          <template #title>
            <feather-icon :icon="tab.icon" size="18" class="mr-50" />
            <span class="font-weight-bold">{{ tab.titleKey }}</span>
            <b-spinner v-if="getTabStatus(index) === 'syncing'" small class="ml-50 tab-sync-spinner" />
            <feather-icon v-else-if="getTabStatus(index) === 'synced'" icon="CheckCircleIcon" size="14"
              class="ml-50 text-success tab-sync-check" />
          </template>
          <component v-if="tab.visible" :is="tab.component" :uuid="panel.panelUuid" :panel-id="currentPanelId"
            :loading="false" :source="source" v-bind="tab.extraProps" @operation-completed="handleOperationCompleted"
            @operation-error="handleOperationError" @template-update="handleTemplateUpdate" />
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import {
  computed,
  ref,
  onMounted,
  getCurrentInstance,
  onBeforeUnmount,
  watch,
  provide,
} from "@vue/composition-api";
import store from "@/store";
import ConfigUsers from "./ConfigUsers.vue";
import ConfigCommunicationPaths from "./ConfigCommunicationPaths/Index.vue";
import ConfigDeviceSetup from "./ConfigDeviceSetup/Index.vue";
import ConfigOutputGroups from "./ConfigOutputGroups.vue";
import ConfigAreaInformation from "./ConfigAreaInformation.vue";
import ConfigAreaSchedules from "./ConfigAreaSchedules.vue";
import ConfigOutputInformation from "./ConfigOutputInformation.vue";
import ConfigZoneInformation from "./ConfigZoneInformation/Index.vue";
import ConfigSystemOptions from "./ConfigSystemOptions.vue";
import ConfigProfiles from "./ConfigProfiles.vue";
import ConfigRemoteOptions from "./ConfigRemoteOptions/Index.vue";
import ConfigNetworkOptions from "./ConfigNetworkOptions.vue";
import ConfigHolidayDates from "./ConfigHolidayDates.vue";
import ConfigOutputOptions from "./ConfigOutputOptions.vue";
import ConfigTimeSchedules from "./ConfigTimeSchedules.vue";
import ConfigBellOptions from "./ConfigBellOptions.vue";
import ConfigMenuDisplay from "./ConfigMenuDisplay.vue";
import ConfigPcLogReport from "./ConfigPcLogReport.vue";
import ConfigStatusList from "./ConfigStatusList.vue";
import ConfigSystemReport from "./ConfigSystemReport.vue";
import { update } from "lodash";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import Swal from "sweetalert2";

export default {
  name: "DmpPanelsConfig",
  components: {
    ConfigUsers,
    ConfigCommunicationPaths,
    ConfigDeviceSetup,
    ConfigOutputGroups,
    ConfigAreaInformation,
    ConfigAreaSchedules,
    ConfigOutputInformation,
    ConfigZoneInformation,
    ConfigSystemOptions,
    ConfigProfiles,
    ConfigRemoteOptions,
    ConfigNetworkOptions,
    ConfigHolidayDates,
    ConfigOutputOptions,
    ConfigTimeSchedules,
    ConfigBellOptions,
    ConfigMenuDisplay,
    ConfigPcLogReport,
    ConfigStatusList,
    ConfigSystemReport
  },
  props: {
    id: [Number, String],
    panelId: [Number, String],
    source: {
      type: String,
      default: "default",
    },
  },
  setup(props) {
    const loading = ref(false);
    const isSavingTemplate = ref(false);
    const panel = ref(null);
    const errorMessage = ref(null);
    const activeTab = ref(0);
    const isOperationActive = computed(() => store.state.dmp_panels.isOperationActive);
    const isDark = computed(() => store.state.appConfig.layout.skin === "dark");
    const isTemplateMode = computed(() => props.source === "template");

    const vm = getCurrentInstance();
    const proxy = vm.root.proxy;
    const toast = useToast();

    const currentPanelId = computed(() => {
      return props.panelId || proxy.$route.params.id;
    });

    const panelAccount = computed(() => (panel.value && panel.value.panel_account) || null);

    const goBack = async () => {
      if (isTemplateMode.value) {
        // Los tabs sin tabla no auto-guardan: confirmar antes de salir para
        // evitar perder cambios no guardados (solo se persisten con "Guardar template").
        const result = await Swal.fire({
          title: proxy.$t("dmp.templateConfig.confirmExitTitle"),
          text: proxy.$t("dmp.templateConfig.confirmExitText"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: proxy.$t("actions.exit"),
          cancelButtonText: proxy.$t("actions.cancel"),
          customClass: {
            confirmButton: "btn btn-primary text-capitalize",
            cancelButton: "btn btn-outline-secondary ml-1 text-capitalize",
          },
          buttonsStyling: false,
        });
        if (!result.isConfirmed) return;
        proxy.$router.push("/dmp/panels");
        return;
      }
      const routeParts = proxy.$route.path.split("/");
      const path = `${routeParts[1]}/${routeParts[2]}`;
      proxy.$router.push(`/${path}`);
    };

    const isTabDisabled = (tabIndex) => {
      return isOperationActive.value && activeTab.value !== tabIndex;
    };

    const getTabStatus = (tabIndex) => {
      return store.getters["dmp_panels/getTabSyncStatus"](tabIndex);
    };

    const tabDataMapping = {
      0: { section: "communication_paths", updateDevices: false },
      1: { section: "network_options", updateDevices: false },
      2: { section: "devices,card_formats", updateDevices: false },
      3: { section: "remote_options", updateDevices: false },
      4: { section: "system_report", updateDevices: false },
      5: { section: "system_options", updateDevices: false },
      6: { section: "bell_options", updateDevices: false },
      7: { section: "output_options", updateDevices: false },
      8: { section: "output_information", updateDevices: true },
      9: { section: "output_groups", updateDevices: false },
      10: { section: "menu_display", updateDevices: false },
      11: { section: "status_list", updateDevices: false },
      12: { section: "pc_log_report", updateDevices: false },
      13: { section: "areas", updateDevices: true, extraSection: "global_area_config" },
      14: { section: "zone_information", updateDevices: false },
      15: { section: "holiday_dates", updateDevices: false },
      16: { section: "time_schedules", updateDevices: false },
      17: { section: "area_schedules", updateDevices: false },
      18: { section: "profiles", updateDevices: false },
      19: { section: "users", updateDevices: false },
    };

    const tabsConfig = computed(() => {
      const p = panel.value;
      return [
        {
          icon: "ServerIcon",
          titleKey: "Communication Paths",
          component: "ConfigCommunicationPaths",
          visible: !!p && !!p.communication_paths,
          extraProps: { "communication-paths-data": (p && p.communication_paths) || [], "panel-account": (p && (p.panel_account || p.panelAccount)) || null },
        },
        {
          icon: "WifiIcon",
          titleKey: "Network Options",
          component: "ConfigNetworkOptions",
          visible: !!p,
          extraProps: { "network-options-data": (p && p.network_options) || {} },
        },
        {
          icon: "SmartphoneIcon",
          titleKey: "Device Setup",
          component: "ConfigDeviceSetup",
          visible: !!p,
          extraProps: {
            "device-setup-data": (p && p.devices) || [],
            "card-formats-data": (p && p.card_formats) || [],
          },
        },
        {
          icon: "RadioIcon",
          titleKey: "Remote Options",
          component: "ConfigRemoteOptions",
          visible: !!p,
          extraProps: { "remote-options-data": (p && p.remote_options) || [] },
        },
        {
          icon: "FileTextIcon",
          titleKey: "System Reports",
          component: "ConfigSystemReport",
          visible: !!p,
          extraProps: { "system-report-data": (p && p.system_report) || {} },
        },
        {
          icon: "SettingsIcon",
          titleKey: "System Options",
          component: "ConfigSystemOptions",
          visible: !!p,
          extraProps: { "system-options-data": (p && p.system_options) || {} },
        },
        {
          icon: "BellIcon",
          titleKey: "Bell Options",
          component: "ConfigBellOptions",
          visible: !!p,
          extraProps: { "bell-options-data": (p && p.bell_options) || {} },
        },
        {
          icon: "SlidersIcon",
          titleKey: "Output Options",
          component: "ConfigOutputOptions",
          visible: !!p,
          extraProps: { "output-options-data": (p && p.output_options) || {} },
        },
        {
          icon: "CodeIcon",
          titleKey: "Output Information",
          component: "ConfigOutputInformation",
          visible: !!p,
          extraProps: { 
            "output-information-data": (p && p.output_information) || [],
            "output-information-config": (p && p.config) || {},
           },
        },
        {
          icon: "GridIcon",
          titleKey: "Output Groups",
          component: "ConfigOutputGroups",
          visible: !!p,
          extraProps: { "output-groups-data": (p && p.output_groups) || [] },
        },
        {
          icon: "MenuIcon",
          titleKey: "Menu Display",
          component: "ConfigMenuDisplay",
          visible: !!p,
          extraProps: { "menu-display-data": (p && p.menu_display) || {} },
        },

        {
          icon: "CheckSquareIcon",
          titleKey: "Status List",
          component: "ConfigStatusList",
          visible: !!p,
          extraProps: { "status-list-data": (p && p.status_list) || {} },
        },

        {
          icon: "FileTextIcon",
          titleKey: "PC Log Reports",
          component: "ConfigPcLogReport",
          visible: !!p,
          extraProps: { "pc-log-report-data": (p && p.pc_log_report) || {} },
        },
        {
          icon: "MapPinIcon",
          titleKey: "Area Information",
          component: "ConfigAreaInformation",
          visible: !!p,
          extraProps: {
            "global-area-config": p.global_area_config || {},
            "areas-data": (p && p.areas) || [],
            "panel-account": (p && (p.panel_account || p.panelAccount)) || null,
          },
        },
        {
          icon: "GridIcon",
          titleKey: "Zone Information",
          component: "ConfigZoneInformation",
          visible: !!p,
          extraProps: { "zone-information-data": (p && p.zone_information) || [] },
        },
        {
          icon: "CalendarIcon",
          titleKey: "Holiday Dates",
          component: "ConfigHolidayDates",
          visible: !!p,
          extraProps: { 
          "holiday-dates-data": (p && p.holiday_dates) || {},
          "holiday-dates-config": (p && p.config) || {},
           },
        },
        {
          icon: "ClockIcon",
          titleKey: "Time Schedules",
          component: "ConfigTimeSchedules",
          visible: !!p,
          extraProps: { "time-schedules-data": (p && p.time_schedules) || [] },
        },
        {
          icon: "CalendarIcon",
          titleKey: "Area Schedules",
          component: "ConfigAreaSchedules",
          visible: !!p,
          extraProps: {
            "area-schedules-data": (p && p.area_schedules) || [],
            "time-schedules-data": (p && p.time_schedules) || [],
          },
        },
        {
          icon: "UserCheckIcon",
          titleKey: "Profiles",
          component: "ConfigProfiles",
          visible: !!p,
          extraProps: {
            "profiles-data": (p && p.profiles) || [],
            "time-schedules-data": (p && p.time_schedules) || [],
            "devices-data": (p && p.devices) || [],
            "is-active": activeTab.value === 18,
          },
        },
        {
          icon: "UsersIcon",
          titleKey: "Users",
          component: "ConfigUsers",
          visible: !!p && !!p.users,
          extraProps: {
            "users-data": (p && p.users) || [],
            "users-config": (p && p.config) || {},
            "profiles-data": (p && p.profiles) || [],
            "is-active": activeTab.value === 19,
          },
        },
      ];
    });

    // Centralized sync queue (serializes all updatePanelData calls)
    const syncQueue = ref([]);
    const isSyncProcessing = ref(false);

    const templateKeys = ref([]);

    // Metadatos de la plantilla que viven fuera del summary. El endpoint
    // update-summary los necesita tanto para crear como para actualizar
    // (homologado con handleCreateFromScratch de SidenavTemplate).
    const templateMeta = ref({ template_name: "", description: "", config: {} });

    // Registro de secciones sin tabla. En modo template ocultan su botón de
    // guardar y registran aquí { validate, collect } para que "Guardar Template"
    // recolecte (forma GET) y valide cada formulario antes del POST.
    const templateSections = new Map();
    provide("registerTemplateSection", (section, handlers) => {
      templateSections.set(section, handlers);
    });
    provide("unregisterTemplateSection", (section) => {
      templateSections.delete(section);
    });

    // section (clave GET) -> índice de tab, para enfocar el tab con error de
    // validación al guardar el template. Incluye extraSection (global_area_config).
    const sectionToTabIndex = computed(() => {
      const map = {};
      Object.entries(tabDataMapping).forEach(([idx, m]) => {
        String(m.section)
          .split(",")
          .forEach((s) => {
            map[s.trim()] = Number(idx);
          });
        if (m.extraSection) map[m.extraSection] = Number(idx);
      });
      return map;
    });

    // Re-normalizamos a {} para que las props de los tabs no fallen el
    // type check de Vue (Expected Object, got Array).
    const OBJECT_FIELDS = [
      "network_options",
      "system_report",
      "system_options",
      "bell_options",
      "output_options",
      "menu_display",
      "status_list",
      "pc_log_report",
      "global_area_config",
      "holiday_dates",
    ];

    const normalizePanelData = (data) => {
      if (!data || typeof data !== "object") return data;
      OBJECT_FIELDS.forEach((k) => {
        if (Array.isArray(data[k])) {
          data[k] = {};
        }
      });
      return data;
    };

    // En modo template no corre getPanelConfig, así que el store getAreasList
    // (que usan Zone Information y Area Schedules) queda vacío. Lo poblamos desde
    // las áreas del propio template. El número de área es `sourceId`, pero
    // getAreasList/ZoneStandard esperan ese número en `.id` (de ahí el mapeo).
    const syncTemplateAreasList = () => {
      if (!isTemplateMode.value) return;
      const areas = (panel.value && panel.value.areas) || [];
      store.commit(
        "dmp_panels/setAreasList",
        areas.map((a) => ({ id: Number(a.sourceId ?? a.id), name: a.name }))
      );
    };

    const loadTemplateData = async (showGlobalLoading = true) => {
      errorMessage.value = null;
      if (showGlobalLoading) loading.value = true;

      // uuid del panel pasado por query desde SidenavTemplate; si no hay, usar string vacío
      // para evitar warnings de prop "uuid" requerida (String) en los hijos.
      const queryUuid = (proxy.$route && proxy.$route.query && proxy.$route.query.uuid) || "";
      // panel_account arrastrado por query desde SidenavTemplate (el JSON de
      // plantilla no lo trae). Solo aplica en modo template.
      const queryAccount = (proxy.$route && proxy.$route.query && proxy.$route.query.account) || "";

      let rawTemplate = null;
      try {
        const response = await store.dispatch("dmp_templates/getAll", {});
        const list = (response && response.data) || [];
        const tpl = list.find(
          (t) => String(t.id) === String(currentPanelId.value)
        );
        rawTemplate = tpl && tpl.summary ? tpl.summary : null;
        if (tpl) {
          templateMeta.value = {
            template_name: tpl.template_name ?? "",
            description: tpl.description ?? "",
            config: tpl.config ?? {},
          };
        }
      } catch (error) {
        console.error("Error loading template data:", error);
        rawTemplate = null;
      }

      if (!rawTemplate) {
        errorMessage.value = proxy.$t("dmp.common.panelNotFoundMessage");
        panel.value = null;
        if (showGlobalLoading) loading.value = false;
        return;
      }

      // Snapshot de las claves originales ANTES de añadir panelUuid runtime
      templateKeys.value = Object.keys(rawTemplate);

      panel.value = normalizePanelData({
        ...rawTemplate,
        panelUuid: rawTemplate.panelUuid || queryUuid,
        panel_account: rawTemplate.panel_account || queryAccount || null,
        config:  templateMeta.value.config,
      });

      // Alimentar el store de áreas para los selects dependientes (zonas, schedules).
      syncTemplateAreasList();

      if (showGlobalLoading) loading.value = false;
    };

    const loadPanelData = async (showGlobalLoading = true) => {
      if (isTemplateMode.value) {
        await loadTemplateData(showGlobalLoading);
        return;
      }

      if (!currentPanelId.value) {
        console.warn("No panel ID available");
        return;
      }

      errorMessage.value = null;

      if (showGlobalLoading) {
        loading.value = true;
      }
      try {
        const panelConfigResponse = await store.dispatch(
          "dmp_panels/getPanelConfig",
          currentPanelId.value
        );

        if (panelConfigResponse.success && panelConfigResponse.data) {
          panel.value = normalizePanelData(panelConfigResponse.data);
          errorMessage.value = null;
        } else {
          console.warn("Panel configuration not found with ID:", currentPanelId.value);
          errorMessage.value = panelConfigResponse.message || proxy.$t("dmp.common.panelNotFoundMessage");
        }
      } catch (error) {
        console.error("Error loading panel data:", error);

        // Capturar el mensaje de error de múltiples fuentes posibles
        if (error.response) {
          if (error.response.data && error.response.data.message) {
            errorMessage.value = error.response.data.message;
          }
          else if (error.response.message) {
            errorMessage.value = error.response.message;
          }
          else if (error.response.statusText) {
            errorMessage.value = `Error ${error.response.status}: ${error.response.statusText}`;
          }
          else {
            errorMessage.value = `Error ${error.response.status}: ${error.message || proxy.$t("dmp.common.errorLoadingPanel")}`;
          }
        } else {
          errorMessage.value = error.message || proxy.$t("dmp.common.errorLoadingPanel");
        }
      } finally {
        if (showGlobalLoading) {
          loading.value = false;
        }
      }
    };

    const processSyncQueue = async () => {
      if (isSyncProcessing.value || syncQueue.value.length === 0) return;

      isSyncProcessing.value = true;
      const item = syncQueue.value.shift();

      store.commit("dmp_panels/setTabSyncStatus", {
        tabIndex: item.tabIndex,
        status: "syncing",
      });

      try {
        await store.dispatch("dmp_panels/updatePanelData", {
          id: currentPanelId.value,
          configSection: item.section,
          updateDevices: item.updateDevices,
        });

        // Sync extra section if defined (areas tab also sync global_area_config)
        if (item.extraSection) {
          await store.dispatch("dmp_panels/updatePanelData", {
            id: currentPanelId.value,
            configSection: item.extraSection,
          });
        }

        await loadPanelData(false);

        store.commit("dmp_panels/setTabSyncStatus", {
          tabIndex: item.tabIndex,
          status: "synced",
        });
      } catch (error) {
        console.error("Error syncing tab:", error);

        if (error.response) {
          if (error.response.data && error.response.data.message) {
            errorMessage.value = error.response.data.message;
          }
          else if (error.response.message) {
            errorMessage.value = error.response.message;
          }
          else if (error.response.statusText) {
            errorMessage.value = `Error ${error.response.status}: ${error.response.statusText}`;
          }
          else {
            errorMessage.value = `Error ${error.response.status}: ${error.message || proxy.$t("dmp.common.errorSyncingTab")}`;
          }
        } else {
          errorMessage.value = error.message || proxy.$t("dmp.common.errorSyncingTab");
        }

        store.commit("dmp_panels/setTabSyncStatus", {
          tabIndex: item.tabIndex,
          status: "error",
        });
      } finally {
        isSyncProcessing.value = false;
        // Process next queued item
        if (syncQueue.value.length > 0) {
          processSyncQueue();
        }
      }
    };

    const enqueueSyncOperation = (tabIndex) => {
      const mapping = tabDataMapping[tabIndex];
      if (!mapping) return;

      // Remove any existing pending entry for this tab to avoid duplicate syncs
      syncQueue.value = syncQueue.value.filter((q) => q.tabIndex !== tabIndex);

      syncQueue.value.push({
        tabIndex,
        section: mapping.section,
        updateDevices: mapping.updateDevices,
        extraSection: mapping.extraSection || null,
      });

      store.commit("dmp_panels/setTabSyncStatus", {
        tabIndex,
        status: "syncing",
      });

      processSyncQueue();
    };

    // Called on tab switch: only sync if not already synced
    const syncTabInfo = (tabIndex) => {
      if (isTemplateMode.value) return;
      const currentStatus = getTabStatus(tabIndex);
      if (currentStatus === "synced" || currentStatus === "syncing") return;
      enqueueSyncOperation(tabIndex);
    };

    // Called by child components after a successful save/delete operation
    const handleOperationCompleted = () => {
      if (isTemplateMode.value) return;
      const tabIndex = activeTab.value;
      // Mark as needing re-sync (the operation changed data, backend cache needs refresh)
      store.commit("dmp_panels/setTabSyncStatus", {
        tabIndex,
        status: "idle",
      });
      enqueueSyncOperation(tabIndex);
    };

    // Template mode only: a tab edited its section locally and emits it back
    // in GET shape. Merge it into panel.value so saveTemplate serializes the
    // whole template identical to the received JSON (no POST, no re-sync).
    const handleTemplateUpdate = (payload) => {
      
      if (!isTemplateMode.value || !payload) return;
      panel.value = { 
        ...panel.value, ...payload ,
      };
      // Si se editaron las áreas, refrescar el store para que los selects
      // dependientes (zonas, schedules) vean los cambios sin recargar.
      if (Object.prototype.hasOwnProperty.call(payload, "areas")) {
        syncTemplateAreasList();
      }
    };

    const saveTemplate = async () => {
      isSavingTemplate.value = true;

      // Secciones sin tabla (sin botón propio en modo template): validar cada
      // formulario y, si todo es válido, volcar su valor (forma GET) a panel.value.
      // Se procesan en orden de tab para enfocar el primero con error.
      const sections = [...templateSections.entries()].sort(
        (a, b) =>
          (sectionToTabIndex.value[a[0]] ?? 0) -
          (sectionToTabIndex.value[b[0]] ?? 0)
      );

      for (const [section, handlers] of sections) {
        const isValid = await handlers.validate();
        if (!isValid) {
          const tabIndex = sectionToTabIndex.value[section];
          if (tabIndex != null) activeTab.value = tabIndex;
          toast({
            component: ToastificationContent,
            props: {
              title: proxy.$t("dmp.templateConfig.saveTemplateError"),
              text: proxy.$t("dmp.templateConfig.saveTemplateValidationError"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          isSavingTemplate.value = false;
          return;
        }
      }

      sections.forEach(([section, handlers]) => {
        panel.value = { ...panel.value, [section]: handlers.collect() };
      });

      // Reconstruir el payload SOLO con las claves del JSON original recibido,
      // dejando fuera campos añadidos en runtime (panelUuid u otros).
      const payload = {};
      templateKeys.value.forEach((k) => {
        payload[k] = panel.value[k];
      });

      try {
        // POST v1/dmp/templates/update-summary ->
        // { id_template, template_name, description, summary, config }
        const response = await store.dispatch("dmp_templates/updateTemplate", {
          id: currentPanelId.value,
          data: payload,
          template_name: templateMeta.value.template_name,
          description: templateMeta.value.description,
          config: panel.value.config,
        });

        if (response && response.success === false) {
          throw new Error(response.message || proxy.$t("dmp.templateConfig.saveTemplateError"));
        }

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.templateConfig.saveTemplateSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
      } catch (error) {
        const apiMessage =
          (error.response && error.response.data && error.response.data.message) ||
          (error && error.message) ||
          proxy.$t("dmp.templateConfig.saveTemplateError");
        toast({
          component: ToastificationContent,
          props: {
            title: apiMessage,
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        isSavingTemplate.value = false;
      }
    };

    const handleOperationError = (error) => {
      if (error && error.response) {
        if (error.response.data && error.response.data.message) {
          errorMessage.value = error.response.data.message;
        } else if (error.response.message) {
          errorMessage.value = error.response.message;
        } else if (error.response.statusText) {
          errorMessage.value = `Error ${error.response.status}: ${error.response.statusText}`;
        } else {
          errorMessage.value = `Error ${error.response.status}: ${error.message}`;
        }
      } else if (typeof error === 'string') {
        errorMessage.value = error;
      } else {
        errorMessage.value = error.message || proxy.$t("dmp.common.errorLoadingPanel");
      }
    };

    watch(activeTab, (newVal) => {
      if (isTemplateMode.value) return;
      syncTabInfo(newVal);
    });

    onMounted(async () => {
      await loadPanelData();
      if (!isTemplateMode.value) {
        syncTabInfo(activeTab.value);
      }
    });

    onBeforeUnmount(() => {
      syncQueue.value = [];
      isSyncProcessing.value = false;
      errorMessage.value = null;
      store.commit("dmp_panels/resetTabSyncStatus");
      store.dispatch("dmp_panels/clearConfig");
    });

    return {
      loading,
      isSavingTemplate,
      panel,
      errorMessage,
      activeTab,
      currentPanelId,
      panelAccount,
      isDark,
      goBack,
      isOperationActive,
      isTabDisabled,
      getTabStatus,
      tabsConfig,
      handleOperationCompleted,
      handleOperationError,
      handleTemplateUpdate,
      saveTemplate,
    };
  },
};
</script>

<style lang="scss" scoped>
.goback-text {
  font-size: 0.857rem;
}

.arrow-icon {
  font-size: 1rem;
}

.alert-dimissible .close {
  position: relative;
  top: unset;
}

.text-sm {
  font-size: 0.857rem;
  word-break: break-word;
}

.nav-left {
  border-right: 1px solid #ebe9f1;
}

.nav-left .nav-link {
  border-radius: 0;
  border: none;
  padding: 1rem 1.5rem;
  color: #6e6b7b;
  font-weight: 500;
}

.nav-left .nav-link.active {
  background-color: rgba(115, 103, 240, 0.12);
  color: #7367f0;
}

.nav-left .nav-link:hover {
  background-color: rgba(115, 103, 240, 0.04);
}

.nav-left .nav-link.disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: auto !important;
}

.nav-left .nav-link.disabled:hover {
  background-color: transparent;
}

.custom-alert-danger {
  background-color: rgba(234, 84, 85, 0.12);
  color: #ea5455;
  padding: 0.4rem 1rem;
}

// Dark mode adjustments
.dark-layout {
  .nav-left {
    border-right-color: #404656;
  }

  .nav-left .nav-link {
    color: #b4b7bd;
  }

  .nav-left .nav-link.active {
    background-color: rgba(115, 103, 240, 0.2);
    color: #7367f0;
  }

  .nav-left .nav-link:hover {
    background-color: rgba(115, 103, 240, 0.08);
  }

  .nav-left .nav-link.disabled:hover {
    background-color: transparent;
  }

  .custom-alert-danger {
    background-color: rgba(234, 84, 85, 0.2);
  }
}

.tab-sync-spinner {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

.tab-sync-check {
  animation: fadeInCheck 0.3s ease-in-out;
}

@keyframes fadeInCheck {
  from {
    opacity: 0;
    transform: scale(0.5);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
